import type { Lesson } from '../../content/types';
import { useApp } from '../../state/store';
import { parseMarkup } from '../Markup';

export function LabPanel({ lesson }: { lesson: Lesson }) {
  const evidence = useApp((s) => s.progress.evidence);
  const runs = useApp((s) => s.runs);
  const active = useApp((s) => s.experiment?.labStepId ?? null);
  const loadLabStep = useApp((s) => s.loadLabStep);
  const gatePassed = useApp((s) => !!s.progress.gates[lesson.gate.id]?.passed);
  const done = lesson.lab.steps.filter((s) => evidence[s.id]).length;

  return (
    <section className="lab" aria-label={`Lab for module ${lesson.number}`}>
      <div className="lab-head">
        <h3>Lab</h3>
        <span className="small">
          {done}/{lesson.lab.steps.length} steps recorded
        </span>
      </div>
      {lesson.lab.instructions && <p className="small muted lab-instructions">{parseMarkup(lesson.lab.instructions, `${lesson.lab.id}-inst`)}</p>}
      {gatePassed && <p className="small muted">This module is complete, so recorded runs are locked. You can still load steps to practise.</p>}
      <ol className="lab-steps">
        {lesson.lab.steps.map((step, i) => {
          const runId = evidence[step.id];
          const run = runId ? runs.find((r) => r.id === runId) : null;
          const isActive = active === step.id;
          const needs = step.inheritControllerFrom && !evidence[step.inheritControllerFrom];
          return (
            <li key={step.id} className={`lab-step${isActive ? ' active' : ''}${runId ? ' done' : ''}`}>
              <div className="lab-step-head">
                <span className="step-num" aria-hidden="true">
                  {runId ? '✓' : i + 1}
                </span>
                <div className="grow">
                  <p className="step-title">
                    {parseMarkup(step.title)}
                    {isActive && <span className="badge run">loaded</span>}
                  </p>
                  <p className="small">{parseMarkup(step.instructions, `${step.id}-i`)}</p>
                  <p className="small muted">Required: {parseMarkup(step.requires, `${step.id}-r`)}</p>
                  <p className="small" aria-live="polite">
                    {runId ? <span className="ok-text">Recorded: Run {run?.number ?? '?'}</span> : <span className="muted">Not recorded yet</span>}
                  </p>
                </div>
                <button
                  type="button"
                  className={`btn ${isActive ? '' : 'primary'}`}
                  disabled={!!needs}
                  onClick={() => {
                    loadLabStep(step.id);
                    window.dispatchEvent(new Event('app:show-sim'));
                  }}
                  aria-label={`Load lab step ${i + 1}: ${step.title}`}
                  title={needs ? 'Complete the tuned-controller step first.' : undefined}
                >
                  {isActive ? 'Reload' : 'Load'}
                </button>
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
